<template>
  <FileImportDialog
    :open="open"
    :account-id="accountId"
    :responsibility-id="responsibilityId"
    :checkpoint-id="checkpointId"
    :start-import="startImport"
    :poll-status="pollStatus"
    :config="config"
    @success="emit('success', $event)"
    @close="emit('close')"
  />
</template>

<script setup>
import FileImportDialog from "@components/FileImportDialog/FileImportDialog.vue";
import {
  formatWordFileSize,
  getWordImportErrorMessage,
  getWordImportJobFailureMessage,
  getWordImportStartErrorMessage,
  validateWordFile,
  WORD_DOCX_MIME_TYPE,
  WORD_IMPORT_MULTIPLE_FILES_MESSAGE,
} from "@components/utils/wordImport";

/**
 * ER-12400 — WordImportDialog (Article Checkpoint import from Word, behind idea-MDP-633).
 * Thin wrapper over FileImportDialog that supplies the Word (.docx) config.
 */
defineProps({
  /** Boolean that controls dialog visibility. */
  open: {
    type: Boolean,
    default: false,
  },
  accountId: {
    type: [Number, String],
    default: null,
  },
  responsibilityId: {
    type: [Number, String],
    default: null,
  },
  checkpointId: {
    type: [Number, String],
    default: null,
  },
  /** POST the multipart import; resolves 202 `{ data: { job_id } }`, rejects 422 with a WORD_IMPORT_* code. */
  startImport: {
    type: Function,
    default: null,
  },
  /** GET the job status; resolves `{ data: { status, progress } }`. */
  pollStatus: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(["success", "close"]);

const config = {
  testId: "word-import-dialog",
  accept: `.docx,${WORD_DOCX_MIME_TYPE}`,
  mimeType: WORD_DOCX_MIME_TYPE,
  fileKind: "DOCX",
  canOpenInViewer: false,
  strings: {
    title: "Import from Word",
    description:
      "Choose a Word document (.docx) to import its text, headings and images into this article.",
    dropzoneHint: "DOCX only (max. 25 MB)",
    inputLabel: "Choose a Word document",
    busyTitle: "Importing from Word",
    startingDescription: "Checking the file and preparing it for import.",
    importingDescription:
      "Reading the document and re-hosting its images. Keep this window open until it finishes.",
    startingStatus: "Reading the document…",
    importingStatus: "Re-hosting images",
    failedTitle: "Import failed",
    failedDescription: "Nothing was added to your article.",
  },
  multipleFilesMessage: WORD_IMPORT_MULTIPLE_FILES_MESSAGE,
  formatFileSize: formatWordFileSize,
  validateFile: validateWordFile,
  getErrorMessage: getWordImportErrorMessage,
  getStartErrorMessage: getWordImportStartErrorMessage,
  getJobFailureMessage: getWordImportJobFailureMessage,
};
</script>
